import {
    XERO_SYNC_CHECK,
    XERO_SYNC_KIND,
    type XeroSyncCheck,
    type XeroSyncKind,
} from '@/features/reports/xero/api';

/*
  Every word the Xero sync tab says (ALS-388). No prototype screen exists for it: the ticket asked
  for one under Reports and the prototype never gained it, so these are ours, in the voice the order
  page's Xero states already use, and are for Franka to confirm.
*/
export const XERO_SYNC_COPY = {
    checks: {
        [XERO_SYNC_CHECK.Documents]: 'Documents',
        [XERO_SYNC_CHECK.AmountsDue]: 'Amounts due',
        [XERO_SYNC_CHECK.Payments]: 'Payments',
        [XERO_SYNC_CHECK.Wallet]: 'Wallet',
        [XERO_SYNC_CHECK.Allocations]: 'Allocations',
        [XERO_SYNC_CHECK.XeroOnly]: 'Xero only',
        [XERO_SYNC_CHECK.Contacts]: 'Contacts',
    } satisfies Record<XeroSyncCheck, string>,
    kinds: {
        [XERO_SYNC_KIND.NotPushed]: 'Not in Xero',
        [XERO_SYNC_KIND.Missing]: 'Missing in Xero',
        [XERO_SYNC_KIND.Voided]: 'Voided in Xero',
        [XERO_SYNC_KIND.TotalDiffers]: 'Total differs',
        [XERO_SYNC_KIND.NumberDiffers]: 'Number differs',
        [XERO_SYNC_KIND.ContactDiffers]: 'Contact differs',
        [XERO_SYNC_KIND.CurrencyDiffers]: 'Currency differs',
        [XERO_SYNC_KIND.DeletedHere]: 'Deleted here, live in Xero',
        [XERO_SYNC_KIND.AmountDue]: 'Amount due differs',
        [XERO_SYNC_KIND.PaymentNotRead]: 'Payment not read back',
        [XERO_SYNC_KIND.PaymentRemoved]: 'Payment deleted in Xero',
        [XERO_SYNC_KIND.Wallet]: 'Wallet differs',
        [XERO_SYNC_KIND.AllocationMissing]: 'Allocation missing in Xero',
        [XERO_SYNC_KIND.AllocationOpen]: 'Credit not allocated',
        [XERO_SYNC_KIND.XeroOnly]: 'Only in Xero',
        [XERO_SYNC_KIND.NoContact]: 'No Xero contact',
        [XERO_SYNC_KIND.ContactMissing]: 'Contact missing in Xero',
        [XERO_SYNC_KIND.ContactArchived]: 'Contact archived in Xero',
    } satisfies Record<XeroSyncKind, string>,
    status: {
        label: 'The last check of Xero',
        checking: 'Checking Xero…',
        lastChecked: (when: string) => `Last checked ${when}`,
        neverChecked: 'Xero has not been checked yet',
        counts: (found: number, fresh: number) =>
            `${found} ${found === 1 ? 'difference' : 'differences'}${fresh > 0 ? `, ${fresh} new` : ''}`,
        next: (when: string) => `Next check ${when}`,
        failed: (problem: string | null, retryAt: string | null) =>
            [
                `The last check could not finish${problem ? `: ${problem.replace(/\.$/, '')}` : ''}.`,
                retryAt ? `It tries again ${retryAt}.` : 'Check again in a moment.',
            ].join(' '),
        disconnected:
            'Xero is not connected, so the check does not run. The list shows what the last check found.',
        checkNow: 'Check now',
        checkNowLabel: 'Check Xero against the admin now',
        started: 'Checking Xero',
        startFailed: 'The check could not start',
        finished: (fresh: number) =>
            fresh > 0
                ? `Xero checked · ${fresh} new ${fresh === 1 ? 'difference' : 'differences'}`
                : 'Xero checked · nothing new',
    },
    tiles: 'Differences by check',
    tileCount: (check: string, count: number) => `${check}: ${count} open`,
    card: {
        title: 'Differences',
        count: (count: number) => `${count} ${count === 1 ? 'difference' : 'differences'}`,
        showing: 'Show',
        open: (count: number) => `Open · ${count}`,
        dismissed: (count: number) => `Dismissed · ${count}`,
    },
    columns: {
        type: 'Difference',
        about: 'Order or client',
        admin: 'Admin says',
        xero: 'Xero says',
        found: 'Found',
        actions: 'Actions',
    },
    openInXero: 'Open in Xero',
    openInXeroLabel: (what: string) => `Open ${what} in Xero, in a new tab`,
    dismiss: 'Dismiss',
    /** Names the difference as well as what it is about: a client can have a wallet and a contact difference at once. */
    dismissLabel: (kind: string, what: string) => `Dismiss ${kind}: ${what}`,
    restore: 'Restore',
    restoreLabel: (kind: string, what: string) => `Restore ${kind}: ${what}`,
    restored: 'Back on the list',
    restoreFailed: 'It could not be put back',
    dismissedBy: (by: string, when: string, reason: string) =>
        `Dismissed by ${by}, ${when}: ${reason}`,
    dialog: {
        title: 'Dismiss this difference',
        body: 'Say why it can be left as it is. The reason is kept with your name, and the difference comes back if the figures change again.',
        reason: 'Reason',
        placeholder: 'Changed in Xero by the accountant, Xero is right',
        required: 'Say why it is dismissed.',
        tooLong: (max: number) => `Keep the reason to ${max} characters.`,
        cancel: 'Cancel',
        confirm: 'Dismiss',
        done: 'Dismissed',
        failed: 'It could not be dismissed',
    },
    empty: {
        never: {
            title: 'Xero has not been checked yet',
            body: (next: string) => `The first check runs ${next}, or check now.`,
            /** While Xero is not connected nothing runs, and nothing can be checked now. */
            disconnected: 'The first check runs once Xero is connected.',
            /** A check ran and could not finish; the status above says why and when it tries again. */
            failed: 'No check has finished yet. The list fills in when one does.',
        },
        agree: {
            title: 'The admin and Xero agree',
            body: (when: string) => `Nothing differed when Xero was last checked, ${when}.`,
        },
        check: {
            title: (check: string) => `Nothing open under ${check}`,
            body: 'The other checks may still list something.',
            action: 'Show every check',
        },
        dismissed: {
            title: 'Nothing dismissed',
            body: 'A difference somebody has settled shows here, with why.',
        },
    },
    failed: {
        title: 'The Xero sync could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
};
